<template>
    <Dialog :open="open" @update:open="handleClose">
        <DialogContent class="sm:max-w-[425px]">
            <DialogHeader>
                <DialogTitle>Delete token</DialogTitle>
            </DialogHeader>

            <div class="space-y-2">
                <div class="space-y-2">
                    <Label for="confirm-text" class="text-sm font-normal text-foreground">
                        Type
                        <span class="font-semibold">"{{ token.name }}"</span>
                        to confirm
                    </Label>
                    <Input
                        id="confirm-text"
                        v-model="confirmText"
                        :placeholder="token.name"
                    />
                </div>

                <div class="flex items-center gap-2 bg-warning-100 px-3 py-2">
                    <TriangleAlert class="size-4 shrink-0 text-warning" />
                    <p class="text-xs leading-4 text-warning">
                        This action will delete this token and all associated
                        instances and data, and is irreversible.
                    </p>
                </div>
            </div>

            <DialogFooter>
                <Button variant="ghost" size="lg" @click="handleClose">Cancel</Button>
                <Button
                    size="lg"
                    class="bg-new-red-100 text-new-red-500 hover:bg-new-red-200"
                    :disabled="confirmText !== token.name || loading"
                    @click="handleDelete"
                >
                    Delete token
                </Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>

<script lang="ts" setup>
import { ref, PropType } from 'vue';
import IToken from '@/types/Plugin/IToken';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { TriangleAlert } from 'lucide-vue-next';

const props = defineProps({
    open: {
        type: Boolean,
        required: true,
    },
    token: {
        type: Object as PropType<IToken>,
        required: true,
    },
    loading: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['close', 'remove', 'update:open']);

const confirmText = ref('');

const handleClose = () => {
    confirmText.value = '';
    emit('close');
    emit('update:open', false);
};

const handleDelete = () => {
    if (confirmText.value === props.token.name) {
        emit('remove');
    }
};
</script>
